import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTheme } from '@reckon-web/theme';
import { Flex } from '@reckon-web/flex';
import { SelectMenu, SelectMenuButton } from '@reckon-web/select-menu';
import { Heading } from '@reckon-web/heading';
import { Box } from '@reckon-web/box';
import { useToasts } from '@reckon-web/toast';
import {
  TabItem,
  TabList,
  useTabList,
  useTabListState,
} from '@reckon-web/tabs';
import { Divider } from '@reckon-web/divider';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';
import { ActionButton } from '@reckon-web/button';
import { Notice } from '@reckon-web/notice';
import { NotificationBadge } from '@reckon-web/badge';

import { StatusBadge } from '../../common/status-badge';
import { useQueryJob } from '../../../hooks/use-query-job';
import { useUpdateJobStatus } from '../../../hooks/use-update-job-status';
import { useGetMyDetails } from '../../../data/useGetMyDetails';
import { canEditJob, canReopenJob } from '../../../utils/user-security-utils';
import { useGetJobComments } from '../../../data/useGetJobComments';
import { UpdateJobInput, useUpdateJob } from '../../../data/useUpdateJob';
import { ViewJobDialogDoCloseArgs } from '../view-job-dialog';
import { getVisibleStatusesFromJobStatuses } from '../../../hooks/use-get-visible-job-statuses';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../../../data/useGetJobStatusList';
import { JobStatusEnum } from '../types/job-status-enum';

import { Options } from './options';
import { SignOffStatus } from './sign-off-status';
import { Details } from './details';
import { Comments } from './comments';
import { Activity } from './activity';

// https://www.typescriptlang.org/docs/handbook/utility-types.html#recordkeystype
// we want an object that has an optional array of strings for each property in TaskUpdateInput
// Partial gets us the optional, Record + keyof gets us the arrays
export type JobUpdateErrors = Partial<Record<keyof UpdateJobInput, string[]>>;
// the properties we will be offering edit on
const editableProperties: Array<keyof UpdateJobInput> = [
  'budget',
  'dueDate',
  'managerUserId',
  'name',
  'partnerUserId',
  'staffUserId',
  'startDate',
  // absolutely unsure about entities
  // we do offer them for edit, but on a drawer
  // if we ever end up giving a validation error for entities
  // but the drawer is not open
  // this'll be bad - the error will get lost
  'entities',
];
// We actually only need this set, but is has to be typed as string so we can check it, or we get
// "Argument of type 'string' is not assignable to parameter of type 'keyof TaskUpdateInput'" if it's typed as keyof TaskUpdateInput
// and I'd REALLY like type safety in the construction of the array above (they keyof is important so property name changes cause compile errors)
// so it's split into two variables
const editablePropertiesSet: Set<string> = new Set(editableProperties);

export const View: React.FC<{
  jobId: string;
  doClose: (args: ViewJobDialogDoCloseArgs) => void;
  setShouldPromptUnsaved: Dispatch<SetStateAction<boolean | undefined>>;
  testId?: string;
}> = ({ jobId, doClose, setShouldPromptUnsaved, testId = `view__index` }) => {
  const theme = useTheme();
  const currentUser = useGetMyDetails();

  const [tabListRef, setTabListRef] = useState<HTMLDivElement | null>(null);
  const selectedTabIndex = useTabListState(tabListRef);
  const { listProps, getTabAttributes, getPanelAttributes } =
    useTabList(selectedTabIndex);

  const [UpdateJobStatusDialog, updateJobStatus] = useUpdateJobStatus();
  const [pendingJobStatus, setPendingJobStatus] =
    useState<GetJobStatusListResult>();

  const jobArgs = useMemo(
    () => ({
      variables: { cloudId: jobId },
    }),
    [jobId]
  );

  const { loading, error, data: job } = useQueryJob(jobArgs);

  const {
    loading: loadingComments,
    error: errorComments,
    data: comments,
  } = useGetJobComments({
    variables: { cloudId: jobId },
  });

  const doUpdateJobStatus = async (status: GetJobStatusListResult) => {
    if (pendingJobStatus || !job) return;
    setPendingJobStatus(status);

    await updateJobStatus({ job, status });

    setPendingJobStatus(undefined);
  };

  const { addToast } = useToasts();

  const canEdit =
    canEditJob(currentUser.data?.user) &&
    job?.status.status !== JobStatusEnum.valuesEnum.Closed;

  const canEditAndDeleteComments =
    job?.status.status !== JobStatusEnum.valuesEnum.Closed;

  // the values we will send up to the server whenever we do an inline edit
  const [jobForUpdate, setJobForUpdate] = useState<UpdateJobInput>();
  // the holding spot for any errors that come back from the server
  const [updateErrors, setUpdateErrors] = useState<JobUpdateErrors>();
  const [updateJob] = useUpdateJob();

  const {
    loading: loadingJobStatuses,
    data: jobStatuses,
    error: errorJobStatuses,
  } = useGetJobStatusList();

  const visibleJobStatuses = useMemo(
    () => getVisibleStatusesFromJobStatuses(jobStatuses),
    [jobStatuses]
  );
  const completedStatus = useMemo(() => {
    return jobStatuses?.find(
      (x) => x.status === JobStatusEnum.valuesEnum.Completed
    );
  }, [jobStatuses]);
  const closedStatus = useMemo(() => {
    return jobStatuses?.find(
      (x) => x.status === JobStatusEnum.valuesEnum.Closed
    );
  }, [jobStatuses]);

  useEffect(() => {
    if (loading || !job) {
      return;
    }
    // setStatus(dataGetTask.status);
    setJobForUpdate({
      cloudId: job.cloudId || '',
      name: job.name || '',
      jobStatusId: job.jobStatusId,
      entities: job.entities || [],
      budget: job.budget,
      managerUserId: job.managerUserId,
      partnerUserId: job.partnerUserId,
      staffUserId: job.staffUserId,
      assigneeUserId: job.assigneeUserId,
      // make sure we capture what originally came over the wire, not the converted value
      // because this is what we need to send back if we update, not the converted value
      dueDate: job.dueDateRaw,
      startDate: job.startDateRaw,
    });
  }, [loading, job]);

  const partialUpdate = useCallback(
    async (vals: Partial<UpdateJobInput>) => {
      if (!jobForUpdate) {
        // I think this one counts as a failure?
        // we didn't update anything, we didn't fail to update anything, we didn't even try to update anything
        return false;
      }

      // check if anything has actually changed
      const hasChanges = Object.entries(vals).some(
        // have to "cast" back to any to get around typescript complaining about
        // No index signature with a parameter of type 'string' was found on type TaskUpdateInput
        ([key, val]) => (jobForUpdate as any)[key] !== val
      );

      // they're not going to change anything, bail
      if (!hasChanges) {
        // this one counts as a success. Sure, we didn't actually update anything
        // but we also didn't fail to update anything
        return true;
      }

      // update the values we'll send to the mutation
      setJobForUpdate((v) => v && Object.assign(v, vals));
      // do the mutation
      const gqlResult = await updateJob({
        variables: { input: jobForUpdate },
      });
      // look for errors
      const validationErrors =
        gqlResult.data?.updateJob?.validationMessage || [];
      if (validationErrors.length === 0) {
        // we're good. No errors
        setUpdateErrors(undefined);
        return true;
      }
      // errors
      const alertMessages = [];
      let hasSpecificErrors = false;
      let hasNonspecificErrors = false;
      const externalErrors: JobUpdateErrors = {};

      for (const message of validationErrors) {
        // work out if this is a field we are allowing edit on or not
        const updateField =
          message.field && editablePropertiesSet.has(message.field)
            ? (message.field as keyof JobUpdateErrors)
            : undefined;
        if (updateField) {
          // it went somewhere, yay. Tuck away the error message for later display
          externalErrors[updateField] = externalErrors[updateField] || [];
          externalErrors[updateField]?.push(message.message || '');
          hasSpecificErrors = true;
        } else {
          // not for any specific field we know about. Might be a cross field message, might be no field at all
          // worst case someone has renamed a field in the graphql but forgot to update the validation messages
          if (message.field && message.message) {
            alertMessages.push(`${message.field}: ${message.message}`);
          } else if (message.message) {
            alertMessages.push(message.message);
          }
          hasNonspecificErrors = true;
        }
      }
      if (!hasSpecificErrors && !hasNonspecificErrors) {
        // got nothing? Probably means the array or one of the objects carrying it itself was null
        alertMessages.push(
          `Workflow failed to edit the job. Try again or contact support.`
        );
      } else {
        // got a mix, or got only specific, or got only non specific
        // any of these cases, put the same message at the end of the array (which might be empty)
        alertMessages.push('Please fix the errors and try again');
      }
      setUpdateErrors(externalErrors);

      addToast({
        title: `Job not edited`,
        message: alertMessages.join(),
        tone: 'critical',
      });

      return false;
    },
    [addToast, jobForUpdate, updateJob]
  );

  // useMemo here because we go through a render loop when a partialUpdate happens
  // which causes the details tab to repopulate the state
  // which puts the old value back on screen temporarily while the mutation happens
  // which looks WHACK
  const tabs = useMemo(() => {
    const result: {
      label: string;
      notification?: number;
      Panel: React.FC<{ isOpen: boolean }>;
    }[] = [
      {
        label: 'Details',
        Panel: () =>
          loading ? (
            <Box marginTop="large">
              <LoadingDots
                label="Loading job"
                data-testid={makeId(testId, 'loading-job')}
              />
            </Box>
          ) : error || !job ? (
            <div>error</div>
          ) : (
            <Details
              job={job}
              partialUpdate={canEdit ? partialUpdate : undefined}
              externalErrors={updateErrors}
              setShouldPromptUnsaved={setShouldPromptUnsaved}
            />
          ),
      },
      {
        label: 'Comments',
        notification: comments
          ? comments?.comments.filter((x) => !x?.wfDeletedOnUTC).length
          : undefined,

        // TODO investigate reloading comments on swapping tabs.
        // if we can detect that we’ve swapped to the comments tab
        // out here somehow (useEffect on selectedIndex maybe, or expose
        // an onSelectedIndexChange from the useTabListState) then we could
        // reload the comments. Not strictly needed though
        Panel: () =>
          loadingComments || loading ? (
            <LoadingDots
              label="Loading comments"
              data-testid={makeId(testId, 'loading-comments')}
            />
          ) : !comments || !job ? (
            <div>Error!</div>
          ) : (
            <Comments
              job={job}
              comments={comments.comments}
              canEditAndDelete={canEditAndDeleteComments}
              testId={makeId(testId, 'comments')}
            />
          ),
      },
      {
        label: 'Activity',
        Panel: ({ isOpen }) => (
          <Activity
            isOpen={isOpen}
            jobId={job?.cloudId}
            testId={makeId(testId, 'activity', job?.cloudId)}
          />
        ),
      },
    ];
    return result;
  }, [
    canEdit,
    canEditAndDeleteComments,
    comments,
    error,
    job,
    loading,
    loadingComments,
    partialUpdate,
    setShouldPromptUnsaved,
    updateErrors,
    testId,
  ]);

  // deliberately not showing the loader if we are loading comments - that'll go in the tab itself
  if (loading || loadingJobStatuses) return <div>Loading...</div>;
  if (
    error ||
    errorComments ||
    errorJobStatuses ||
    !completedStatus ||
    !closedStatus ||
    !job
  ) {
    return <div>Error!</div>;
  }

  const jobStatusReadonly =
    job.status.status === JobStatusEnum.valuesEnum.Closed ||
    (job.status && !canEditJob(currentUser.data?.user));

  return (
    <React.Fragment>
      <Flex direction="column" width="100%" data={{ testid: testId }}>
        <Flex
          direction="column"
          gap="large"
          paddingX="xlarge"
          paddingTop="large"
          flexShrink={0}
        >
          <Flex alignItems="center" gap="small">
            <Options job={job} doClose={doClose} />
          </Flex>
          <Heading
            level="2"
            css={{
              // stop LONG text (usually links, but anything really) from busting out sideways
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {job?.name}
          </Heading>
          <Flex alignItems="center" gap="small">
            {jobStatusReadonly ? (
              <StatusBadge
                label={job.status.name}
                colour={job.status.colour}
                data-testid={makeId(testId, 'job-status-badge')}
              />
            ) : (
              <div css={{ marginLeft: `-${theme.spacing.small}px` }}>
                <SelectMenu
                  trigger={({ triggerProps, hasValue }) => (
                    <SelectMenuButton
                      weight="subtle"
                      hasValue={hasValue}
                      {...triggerProps}
                      data-testid={makeId(testId, 'select-menu')}
                    >
                      {pendingJobStatus ? (
                        <div css={{ paddingLeft: theme.spacing.xxsmall }}>
                          <LoadingDots
                            label="pending"
                            size="small"
                            data-testid={makeId(testId, 'pending')}
                          />
                        </div>
                      ) : job.status ? (
                        <StatusBadge
                          label={job.status.name}
                          colour={job.status.colour}
                          data-testid={makeId(
                            testId,
                            'pending-job-status-badge'
                          )}
                        />
                      ) : (
                        'Status'
                      )}
                    </SelectMenuButton>
                  )}
                  options={visibleJobStatuses || []}
                  itemToLabel={(x) => x.name || ''}
                  itemToValue={(x) => x.cloudId}
                  itemRenderer={(status) => (
                    <StatusBadge label={status.name} colour={status.colour} />
                  )}
                  value={job.status}
                  onChange={doUpdateJobStatus}
                  css={{
                    button: {
                      // Overriding styles of button to look more balanced
                      paddingLeft: theme.spacing.small,
                      height: theme.sizing.base,
                    },
                  }}
                />
              </div>
            )}
            {job.status.status === JobStatusEnum.valuesEnum.Completed && (
              <ActionButton
                onClick={() => doUpdateJobStatus(closedStatus)}
                label="Close job"
                weight="bold"
                data-testid={makeId(testId, 'close-job')}
                disabled={Boolean(pendingJobStatus)}
              />
            )}
          </Flex>
          {job.status.status === JobStatusEnum.valuesEnum.Closed &&
            canReopenJob(currentUser.data?.user) && (
              <Flex>
                <Notice
                  tone="informative"
                  title="This job is closed"
                  data-testid={makeId(testId, 'notice-closed-job')}
                >
                  <ActionButton
                    onClick={() => doUpdateJobStatus(completedStatus)}
                    label="Re-open"
                    weight="bold"
                    data-testid={makeId(testId, 're-open')}
                    disabled={Boolean(pendingJobStatus)}
                  />
                </Notice>
              </Flex>
            )}
          {job.status.status === JobStatusEnum.valuesEnum.Closed &&
            !canReopenJob(currentUser.data?.user) && (
              <Flex>
                <Notice
                  tone="informative"
                  title="This job is closed"
                  data-testid={makeId(testId, 'notice-closed-job')}
                >
                  Only an administrator may re-open this job
                </Notice>
              </Flex>
            )}
          <SignOffStatus job={job} />
        </Flex>
        <Flex flexGrow={1} direction="column" css={{ overflow: 'hidden' }}>
          <Flex paddingX="xlarge" flexShrink={0}>
            <TabList {...listProps} ref={setTabListRef}>
              {tabs.map(({ label, notification }, index) => {
                return (
                  <TabItem
                    key={label}
                    isSelected={index === selectedTabIndex}
                    {...getTabAttributes(index)}
                    data-testid={makeId(testId, 'tab-item')}
                  >
                    {label}
                    {notification ? (
                      <NotificationBadge value={notification} tone="critical" />
                    ) : null}
                  </TabItem>
                );
              })}
            </TabList>
          </Flex>
          <Divider />
          {tabs.map(({ Panel, label }, index) => (
            <div
              key={label}
              {...getPanelAttributes(index)}
              css={{
                overflowY: 'auto',
                overflowX: 'hidden',
                // Comments tab (index 1) doesn't get padding, it needs to be full bleed, it needs more complicated layout
                paddingLeft: index === 1 ? 0 : theme.spacing.xlarge,
                paddingRight: index === 1 ? 0 : theme.spacing.xlarge,
              }}
            >
              <Panel isOpen={!getPanelAttributes(index).hidden} />
            </div>
          ))}
        </Flex>
      </Flex>
      <UpdateJobStatusDialog />
    </React.Fragment>
  );
};
